import { makeStyles } from '@mui/styles';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'react-phone-input-2/lib/style.css';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthFlow, useAuth } from '../../hooks/useAuth';
import { useDomain } from '../../hooks/useDomain';
import { getTheme } from '../../stores/selectors/theme.selector';
import { ROUTES } from '../../types/routes.enum';
import { showSnackBar } from '../../util/toast.utility';
import { CodeVerification } from '../code-verification/code-verification.component';
import { BackButtonHeader } from '../ui-components/back-button-header.component';
import NetworkLogo from '../ui-components/network-logo.component';
import { ViewWrapper } from '../ui-components/view-wrapper.component';
import LoginForm from './login-form.component';
import { getIsEmail, getLoginString } from './utils';
import { CodeError } from '../../constant/text.constant';

const useStyle = makeStyles(() => ({
  wrapper: {
    width: '100%',
    height: '100%',
    margin: 'auto',
  },
}));

const Login = () => {
  const navigate = useNavigate();
  const classes = useStyle();

  const { i18n } = useTranslation();
  const [phoneNumberOrEmailLogin, setPhoneNumberOrEmailLogin] = useState('');
  const [codeSentToEmail, setCodeSentToEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newUser, setNewUser] = useState(null);

  const [confirmCode, setConfirmCode] = useState(false);
  const location = useLocation();
  const chargerName = new URLSearchParams(location.search).get('charger');
  const association = new URLSearchParams(location.search).get('associationId');
  const theme = useSelector(getTheme);

  const auth = useAuth();
  const { domain } = useDomain();

  const isHomeId = useMemo(() => {
    return location?.pathname?.toLowerCase().includes(`${ROUTES.LOGIN_HOME_ID}`);
  }, [location]);

  useEffect(() => {
    if (auth.isLoggedIn) {
      if (chargerName) {
        navigate(`/${chargerName}`);
      } else if (isHomeId) {
        const url = association
          ? `/app/${domain}/${ROUTES.ASSOCIATION}?associationId=${association}`
          : `/app/${domain}/${ROUTES.ASSOCIATION}`;
        navigate(url);
      } else {
        navigate(`/app/${domain}/${ROUTES.ACCOUNT}`);
      }
    }
  }, [auth?.isLoggedIn, chargerName]);

  const showRegisterInsideLogin = () => {
    const route = isHomeId
      ? association
        ? `/app/${domain}/${ROUTES.REGISTER_HOME_ID}?associationId=${association}`
        : `/app/${domain}/${ROUTES.REGISTER_HOME_ID}`
      : chargerName
        ? `/app/${domain}/${ROUTES.REGISTER}?charger=${chargerName}`
        : `/app/${domain}/${ROUTES.REGISTER}`;
    navigate(route);
  };

  const handleKeyPress = async (event) => {
    if (event.key === 'Enter') {
      await signIn();
    }
  };

  const signIn = async () => {
    try {
      setLoading(true);

      const isEmail = getIsEmail(phoneNumberOrEmailLogin);
      const loginString = getLoginString(phoneNumberOrEmailLogin);

      const result = await auth.signIn(loginString);
      // If the authentication flow used was OTP, show the code verification component
      if (result?.flow === AuthFlow.OTP) {
        setNewUser(result?.user);
        setCodeSentToEmail(isEmail || !loginString.startsWith('+1'));
        setConfirmCode(true);
      }
    } catch (error) {
      setLoading(false);
      if (error.code === CodeError.USER_NOT_FOUND) {
        showSnackBar(i18n.t('no_account_email_or_phone'));
      } else if (error.code === CodeError.USER_AUTHENTICATION_FAILED) {
        showSnackBar(i18n.t('authentication_failed'));
      } else if (
        error.code === CodeError.INVALID_EMAIL_OR_PHONE ||
        error.code === CodeError.INVALID_EMAIL_DOMAIN
      ) {
        showSnackBar(i18n.t('invalid_email_or_phone'));
      } else {
        showSnackBar(i18n.t('unknown_error'));
      }
    }
  };

  const handleClosebutton = () => {
    navigate(`/${chargerName}`);
  };

  return (
    <ViewWrapper>
      <div className={classes.wrapper}>
        <BackButtonHeader
          isSpaceBetweenAlign
          onBackArrowClicked={handleClosebutton}
          companyIcon={<NetworkLogo height='28px' width='auto' />}
        />
        {!confirmCode ? (
          <LoginForm
            handleKeyPress={handleKeyPress}
            loading={loading}
            theme={theme}
            signIn={signIn}
            setPhoneNumberOrEmailLogin={setPhoneNumberOrEmailLogin}
            showRegisterInsideLogin={showRegisterInsideLogin}
          />
        ) : (
          <CodeVerification
            theme={theme}
            phoneNumberOrEmailLogin={getLoginString(phoneNumberOrEmailLogin)}
            loginIsEmail={getIsEmail(phoneNumberOrEmailLogin)}
            codeSentToEmail={codeSentToEmail}
            chargerName={chargerName}
            user={newUser}
          />
        )}
      </div>
    </ViewWrapper>
  );
};

export default Login;
